import { ApolloProvider } from '@apollo/client';
import I18nProvider from 'carbon-react/lib/components/i18n-provider';
import GlobalStyle from 'carbon-react/lib/style/global-style';
import Aegean from 'carbon-react/lib/style/themes/aegean';
import { ConnectedRouter } from 'connected-react-router';
import I18njs from 'i18n-js';
import { PropsWithChildren } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { LaunchDarklyProvider } from './analytics/LaunchDarklyProvider';
import ToastContainer from './components/ToastContainer';
import { useAppApolloClient } from './graphql';
import locales from './locales';
import { selectUserId } from './redux/selectors';
import { history, store } from './redux/store';
import { updateRollbar } from './rollbar';
import { NotificationHandlerProvider } from './components/Notifications/NotificationProvider';

I18njs.translations.en = locales.en;
I18njs.defaultLocale = 'en';

type AppProviderProps = Record<string, unknown>;

function AppProvidersWithRouting({ children }: PropsWithChildren<AppProviderProps>) {
  const location = useLocation();
  // Some routes specifically do not want to initialize Apollo because their authentication mechanism implementation
  // conflicts with the server's requirements for authentication. I.e., the server requires bearer token authentication
  // for GraphQL while some routes require SAML.
  // Since the SAML routes don't use Apollo at all anyways, don't initialize the client.
  const skipApolloInitialization = ['/intacct-admin', '/saml'].some((pathname) => location.pathname.startsWith(pathname));
  const apolloClient = useAppApolloClient(skipApolloInitialization);
  return skipApolloInitialization ? (
    <>{children}</>
  ) : (
    <ApolloProvider client={apolloClient!}>
      <NotificationHandlerProvider>{children}</NotificationHandlerProvider>
    </ApolloProvider>
  );
}

function AppProvidersWithStore({ children }: PropsWithChildren<AppProviderProps>) {
  const userId = useSelector(selectUserId);
  updateRollbar({ userId });

  return (
    <BrowserRouter>
      <ConnectedRouter history={history}>
        {/* I18nProvider is not typed properly */}
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <I18nProvider locale={locales.en}>
          <DndProvider backend={HTML5Backend}>
            <LaunchDarklyProvider>
              <ThemeProvider theme={Aegean}>
                <GlobalStyle />
                <ToastContainer />
                <AppProvidersWithRouting>{children}</AppProvidersWithRouting>
              </ThemeProvider>
            </LaunchDarklyProvider>
          </DndProvider>
        </I18nProvider>
      </ConnectedRouter>
    </BrowserRouter>
  );
}

export default function AppProviders({ children }: PropsWithChildren<AppProviderProps>): JSX.Element {
  return (
    <Provider store={store}>
      <AppProvidersWithStore>{children}</AppProvidersWithStore>
    </Provider>
  );
}
